import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { fetchClientOrganizationsSpecial } from "../../../Apis/Client";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { getCoreScopeKey } from "../../../Configurations/coreSession";
import ListSummaryCards, {
  distinctCount,
} from "../../../Components/List/ListSummaryCards";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
    sortBy: "organization_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          const parts = filterValue.split(",").map((v) => v.trim());
          const isNumericIdField = /_id$/.test(field) || field === "client_id";
          filterValue = isNumericIdField
            ? parts.map((v) => String(Number(v)))
            : parts.map((v) => v.toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

// Backend sometimes returns: ["metaData", { success, data, pagination, ... }]
// Normalize it so the rest of the code can rely on an object payload.
const normalizeApiPayload = (result) => {
  if (!result) return null;
  if (!Array.isArray(result)) return result;

  const payload =
    result.find((x) => x && typeof x === "object" && "success" in x) ||
    result.find((x) => x && typeof x === "object" && "data" in x) ||
    result[1] ||
    null;

  return payload && typeof payload === "object" ? payload : null;
};

/** Supports filtered POST `{ success, data: [] }` and GET `{ message, data: { data: [] } }`. */
const extractOrganizationRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  if (Array.isArray(payload.organizations)) return payload.organizations;
  return [];
};

const extractPagination = (payload, searchParams) => {
  const nested = payload?.data;
  const fromFiltered = payload?.pagination;
  if (fromFiltered) {
    const pageSize = fromFiltered.pageSize || fromFiltered.limit || 10;
    return {
      page:
        fromFiltered.page ||
        Number.parseInt(searchParams.get("page"), 10) ||
        1,
      pageSize,
      total: fromFiltered.total || 0,
      totalPages: fromFiltered.totalPages || 1,
      hasNext: Boolean(fromFiltered.hasNext),
      hasPrev: Boolean(fromFiltered.hasPrev),
    };
  }
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return {
      page: nested.current_page || Number.parseInt(searchParams.get("page"), 10) || 1,
      pageSize: nested.per_page || Number.parseInt(searchParams.get("pageSize"), 10) || 10,
      total: nested.total || 0,
      totalPages: nested.last_page || 1,
      hasNext: Boolean(nested.next_page_url),
      hasPrev: Boolean(nested.prev_page_url),
    };
  }
  return { ...DEFAULT_PAGINATION };
};

const getClientIdFromSearchParams = (searchParams) => {
  const raw =
    searchParams.get("filter_client_id") ?? searchParams.get("client_id");
  if (raw == null || raw === "") return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
};

const sortOrganizationRows = (rows, sortBy, sortOrder) => {
  const dir = sortOrder === "asc" ? 1 : -1;
  const field = sortBy || "organization_id";

  return [...rows].sort((a, b) => {
    const av = a?.[field];
    const bv = b?.[field];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, {
      numeric: true,
      sensitivity: "base",
    }) * dir;
  });
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const EDITION_COLUMN = {
  field: "edition_name",
  label: "Edition",
  visible: true,
  width: 140,
  filterable: true,
  sortable: true,
  pinned: "none",
  required: false,
};

export const DEFAULT_COLUMNS = [
  {
    field: "organization_name",
    label: "Organization Name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "organization_short_name",
    label: "Organization Short Name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  EDITION_COLUMN,
];

const withEditionColumn = (columns) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    return DEFAULT_COLUMNS;
  }
  if (columns.some((column) => column.field === "edition_name")) {
    return columns;
  }
  return [...columns, EDITION_COLUMN];
};

  function ClientOrganizationList() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const { userData } = useAuthStore();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const gridScopeKey = getCoreScopeKey(userData);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // After add/edit save, the form navigates back with refreshOrganizations state.
  // The list stays mounted in the drawer layout, so bump a key to refetch.
  // Keep pathname/search unchanged — only clear the one-shot state.
  useEffect(() => {
    if (!location.state?.refreshOrganizations) return;
    setListRefreshKey((key) => key + 1);
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: null }
    );
  }, [
    location.state,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      try {
        const configRes = await fetchClientOrganizationsSpecial();
        if (configRes.success && configRes.data) {
          const datagrids = configRes.data || [];
          const datagridKey = `client_organizations_grid_${gridScopeKey}`;
          const savedConfig = datagrids.find(
            (dg) => dg.client_organization_key === datagridKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.client_organization_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(withEditionColumn(serverCfg.columns));
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [gridScopeKey]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load functional role specialization data
  useEffect(() => {
    if (!hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const page = Number.parseInt(searchParams.get("page"), 10) || 1;
        const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;
        const clientId = getClientIdFromSearchParams(searchParams);
        const requestBody = {
          filters,
          page,
          pageSize,
          sort: {
            field: searchParams.get("sortBy") || "organization_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        let rows = [];
        let pagination = { ...DEFAULT_PAGINATION };

        /** Same source as client reports — always filtered by client_id. */
        const loadViaClientContext = async () => {
          const params = new URLSearchParams({
            client_id: String(clientId),
          });
          if (search?.trim()) {
            params.set("search", search.trim());
          }

          const res = await fetch(
            `${MAIN_URL}/api/client-management/clients/with-users-organizations?${params.toString()}`,
            {
              method: "GET",
              headers: authHeaders(),
              signal: fetchControllerRef.current.signal,
            }
          );

          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          const json = await res.json();
          if (json?.success === false) {
            throw new Error(json.message || "Failed to load client organizations.");
          }

          const sortBy = searchParams.get("sortBy") || "organization_id";
          const sortOrder = searchParams.get("sortOrder") || "desc";
          const allRows = sortOrganizationRows(
            Array.isArray(json.organizations) ? json.organizations : [],
            sortBy,
            sortOrder
          );

          const total = allRows.length;
          const totalPages = Math.max(1, Math.ceil(total / pageSize));
          const start = (page - 1) * pageSize;
          rows = allRows.slice(start, start + pageSize);
          pagination = {
            page,
            pageSize,
            total,
            totalPages,
            hasNext: page < totalPages,
            hasPrev: page > 1,
          };
        };

        const loadViaGet = async () => {
          const params = new URLSearchParams({
            page: String(page),
            per_page: String(pageSize),
          });
          if (clientId != null) {
            params.set("client_id", String(clientId));
          }
          if (search?.trim()) {
            params.set("search", search.trim());
          }

          const getRes = await fetch(
            `${MAIN_URL}/api/client-management/organizations?${params.toString()}`,
            {
              method: "GET",
              headers: authHeaders(),
              signal: fetchControllerRef.current.signal,
            }
          );

          if (!getRes.ok) {
            throw new Error(`HTTP error! status: ${getRes.status}`);
          }

          const getJson = await getRes.json();
          const getPayload = normalizeApiPayload(getJson) || getJson;
          rows = extractOrganizationRows(getPayload);
          pagination = extractPagination(getPayload, searchParams);

          if (clientId != null) {
            const filtered = rows.filter(
              (item) => Number(item?.client_id) === Number(clientId)
            );
            const apiIgnoredClientFilter =
              filtered.length !== rows.length ||
              (pagination.total > filtered.length && filtered.length > 0);
            if (apiIgnoredClientFilter) {
              await loadViaClientContext();
            } else {
              rows = filtered;
              pagination = {
                ...pagination,
                total: filtered.length,
                totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
              };
            }
          }
        };

        const loadViaFilteredPost = async () => {
          const response = await fetch(
            `${MAIN_URL}/api/client-management/organizations/filtered`,
            {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify(requestBody),
              signal: fetchControllerRef.current.signal,
            }
          );

          if (!response.ok) {
            return false;
          }

          const result = await response.json();
          const payload = normalizeApiPayload(result) || result;
          rows = extractOrganizationRows(payload);
          pagination = extractPagination(payload, searchParams);
          return payload?.success !== false && rows.length >= 0;
        };

        // From client reports: load orgs for that client only (with-users-organizations).
        if (clientId != null) {
          await loadViaClientContext();
        } else {
          try {
            const postOk = await loadViaFilteredPost();
            if (!postOk || rows.length === 0) {
              await loadViaGet();
            }
          } catch (filteredErr) {
            if (filteredErr.name === "AbortError") {
              throw filteredErr;
            }
            await loadViaGet();
          }
        }

        const b = rows.map((item) => ({
          ...item,
          id: item.organization_id,
          organization_name: item?.organization_name || "-",
          organization_short_name: item?.organization_short_name || "-",
          edition_name: item?.edition_name || "-",
        }));

        setOrganizations(b);
        setPaginationData(pagination);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching Client Organizations:", error);
        toast.error(
          error.message || "Failed to load client organizations."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [searchParams, listRefreshKey]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/client-management/organizations/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const result = await response.json().catch(() => ({}));

        if (response.ok) {
          toast.success(result.message || "Organization deleted successfully");
          // Refresh data by updating search params
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              result.message ||
              (typeof result.error === "string" ? result.error : null) ||
              result.errors?.[Object.keys(result.errors || {})[0]]?.[0] ||
              "Failed to delete Organization";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Something went wrong. Please try again later.");
      }
    },
    [searchParams, setSearchParams],
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate({
        pathname: `/organization/client/organizations/edit/${item.id}`,
        search: location.search,
      });
    },
    [navigate, location.search]
  );

  const handleShow = useCallback(
    (item) => {
      navigate({
        pathname: `/organization/client/organizations/view/${item.id}`,
        search: location.search,
      });
    },
    [navigate, location.search]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total organizations",
        value: paginationData?.total ?? organizations.length,
        iconKey: "building",
      },
      {
        key: "editions",
        label: "Editions on page",
        value: distinctCount(organizations, "edition_name"),
        iconKey: "layers",
      },
      {
        key: "names",
        label: "Named on page",
        value: distinctCount(organizations, "organization_name"),
        iconKey: "hash",
      },
    ],
    [organizations, paginationData]
  );

  return (
    <>
      <Layout4
        loading={loading}
        delete_action={"CLIENT_ORGANIZATION_DELETE"}
        heading={"Client Organizations"}
        btnName={"Add Organization"}
        Data={organizations}
       
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          " Organization Management",
          " Client Organizations",
          "Add  Organizations",
          "Organization Management",
        ]}
        Route={"/organization/client/organizations"}
        setData={setOrganizations}
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        token={localStorage.getItem("token")}
      />

      <ListSummaryCards cards={summaryCards} />

      {!loadingConfig && (
        <TableDataGeneric
            tableName="organizations"
          primaryKey="organization_id"
          heading="Client Organizations"
          data={organizations}
          sortname="organization_id"
          showActions={true}
          linkType="organization name"
          onclickRow={handleEdit}
          Route="/organization/client/organizations"
          DeleteFunc={handleDelete}
          handleShow={handleShow}
          edit_delete_action={["CLIENT_ORGANIZATION_EDIT", "CLIENT_ORGANIZATION_DELETE"]}
          EditFunc={handleEdit}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          onSortChange={handleSortChange}
          filterModule="organizations"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default ClientOrganizationList;
